import { buildSearchParams, urlBuilder } from '@starjack/utils';

import { BackendType, FrontendType, ObjectType } from '@starjack/utils';
import { baseApi } from '@starjack/api';
import { convertTicketToFrontend, Ticket } from '../model/ticket.types';
import { formatStarjackDateToBackendString } from '@starjack/utils';

export type GetTicketsSearchParams<T extends ObjectType = FrontendType> = {
    userId?: string;
    orderId?: string;
    query?: string;
    orderDateFrom?: T extends FrontendType ? Date : string;
    orderDateTo?: T extends FrontendType ? Date : string;
    ticketStartFrom?: T extends FrontendType ? Date : string;
    ticketStartTo?: T extends FrontendType ? Date : string;
    sort?: string;
    size?: number;
    page?: number;
    hasInsurance?: boolean;
};

export const convertGetTicketsSearchParamsToBackend = (
    params: GetTicketsSearchParams
): GetTicketsSearchParams<BackendType> => ({
    ...params,
    orderDateFrom: formatStarjackDateToBackendString(params.orderDateFrom),
    orderDateTo: formatStarjackDateToBackendString(params.orderDateTo),
    ticketStartFrom: formatStarjackDateToBackendString(params.ticketStartFrom),
    ticketStartTo: formatStarjackDateToBackendString(params.ticketStartTo),
});

export type GetTicketsResponse<T extends ObjectType = FrontendType> = {
    content: Ticket<T>[];
    size: number;
    totalElements: number;
    totalPages: number;
};

const ticketUrls = (id = '') => ({
    tickets: urlBuilder('order', 'tickets'),
    ticketsPaged: urlBuilder('order', 'liftoperators/tickets-paged'),
    ticketCancel: urlBuilder('order', `tickets/${id}/cancel`),
    ticketPermissionStatus: urlBuilder('bei', `tickets/${id}`),
});

export const ticketApi = baseApi
    .enhanceEndpoints({ addTagTypes: ['TICKETS'] })
    .injectEndpoints({
        endpoints: (builder) => ({
            getTickets: builder.query<
                GetTicketsResponse,
                GetTicketsSearchParams
            >({
                query: (params) => {
                    const paramsCopy = { ...params };

                    if (!paramsCopy.hasInsurance) {
                        delete paramsCopy.hasInsurance;
                    }

                    return `${ticketUrls().ticketsPaged}${buildSearchParams(
                        convertGetTicketsSearchParamsToBackend(paramsCopy)
                    )}`;
                },

                transformResponse: (
                    response: GetTicketsResponse<BackendType>
                ) => {
                    return {
                        ...response,
                        content: response.content.map(convertTicketToFrontend),
                    };
                },
                providesTags: ['TICKETS'],
            }),

            postTicketCancellation: builder.mutation<
                void,
                { ticketId: string; refund: boolean; reason?: string }
            >({
                query: ({ ticketId, refund, reason }) => ({
                    url: `${ticketUrls(ticketId).ticketCancel}`,
                    method: 'POST',
                    body: {
                        ticketId,
                        refund,
                        reason,
                    },
                }),
                invalidatesTags: ['TICKETS'],
            }),

            postTicketCancelInsurance: builder.mutation<
                Response,
                { ticketId: string }
            >({
                query: ({ ticketId }) => ({
                    url: `${ticketUrls().tickets}/${ticketId}/cancel-insurance`,
                    method: 'POST',
                    body: { ticketId },
                }),
                invalidatesTags: ['TICKETS'],
            }),

            postTicketRevokeInsurance: builder.mutation<
                Response,
                { ticketId: string }
            >({
                query: ({ ticketId }) => ({
                    url: `${ticketUrls().tickets}/${ticketId}/revoke-insurance`,
                    method: 'POST',
                    body: { ticketId },
                }),
                invalidatesTags: ['TICKETS'],
            }),

            postTicketCancelInsuranceFromInsurance: builder.mutation<
                Response,
                { ticketId: string }
            >({
                query: ({ ticketId }) => ({
                    url: `${ticketUrls().tickets}/${ticketId}/embargo-insurance`,
                    method: 'POST',
                    body: { ticketId },
                }),
                invalidatesTags: ['TICKETS'],
            }),

            getTicketPermissionStatus: builder.query<
                { id: string; permissionStatuses: string[] },
                { ticketId: string }
            >({
                query: ({ ticketId }) =>
                    ticketUrls(ticketId).ticketPermissionStatus,
                providesTags: ['TICKETS'],
            }),
        }),
    });

export const {
    useGetTicketsQuery,
    usePostTicketCancellationMutation,
    usePostTicketCancelInsuranceMutation,
    usePostTicketRevokeInsuranceMutation,
    usePostTicketCancelInsuranceFromInsuranceMutation,
    useGetTicketPermissionStatusQuery,
} = ticketApi;
